import { useState, useCallback } from "react";
import type { EvaluationCriterion, CreateCriterionPayload, UpdateCriterionPayload } from "../../../../types/criteria.type";
import { EMPTY_FORM, type CriterionFormValues } from "./criteria-form.types";

interface UseCriterionFormOptions {
	addCriterion: (payload: CreateCriterionPayload) => Promise<EvaluationCriterion | null>;
	editCriterion: (criterionId: number, payload: UpdateCriterionPayload) => Promise<EvaluationCriterion | null>;
}

export interface UseCriterionFormResult {
	isAdding: boolean;
	editingId: number | null;
	formValues: CriterionFormValues;
	formError: string | null;
	setFormValues: React.Dispatch<React.SetStateAction<CriterionFormValues>>;
	updateField: (field: keyof CriterionFormValues, value: string) => void;
	handleStartAdd: () => void;
	handleStartEdit: (criterion: EvaluationCriterion) => void;
	handleCancel: () => void;
	handleSaveNew: () => Promise<void>;
	handleSaveEdit: () => Promise<void>;
}

export const useCriterionForm = ({
	addCriterion,
	editCriterion,
}: UseCriterionFormOptions): UseCriterionFormResult => {
	const [isAdding, setIsAdding] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [formValues, setFormValues] = useState<CriterionFormValues>(EMPTY_FORM);
	const [formError, setFormError] = useState<string | null>(null);

	const validateForm = useCallback((): boolean => {
		if (!formValues.name.trim()) {
			setFormError("Name is required");
			return false;
		}
		const weight = Number(formValues.weight);
		if (isNaN(weight) || weight < 0 || weight > 100) {
			setFormError("Weight must be between 0 and 100");
			return false;
		}
		setFormError(null);
		return true;
	}, [formValues.name, formValues.weight]);

	const handleStartAdd = useCallback((): void => {
		setIsAdding(true);
		setEditingId(null);
		setFormValues(EMPTY_FORM);
		setFormError(null);
	}, []);

	const handleStartEdit = useCallback((criterion: EvaluationCriterion): void => {
		setEditingId(criterion.id);
		setIsAdding(false);
		setFormValues({
			name: criterion.name,
			description: criterion.description ?? "",
			rfx_evaluation_type_id: criterion.rfx_evaluation_type_id,
			weight: String(criterion.weight),
		});
		setFormError(null);
	}, []);

	const handleCancel = useCallback((): void => {
		setIsAdding(false);
		setEditingId(null);
		setFormValues(EMPTY_FORM);
		setFormError(null);
	}, []);

	const updateField = useCallback(
		(field: keyof CriterionFormValues, value: string): void => {
			setFormValues((prev) => ({ ...prev, [field]: value }));
			setFormError(null);
		},
		[],
	);

	const handleSaveNew = useCallback(async (): Promise<void> => {
		if (!validateForm()) return;

		const payload: CreateCriterionPayload = {
			name: formValues.name.trim(),
			description: formValues.description.trim() || undefined,
			rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
			weight: Number(formValues.weight),
		};
		const result = await addCriterion(payload);
		if (result) {
			setIsAdding(false);
			setFormValues(EMPTY_FORM);
		}
	}, [validateForm, formValues, addCriterion]);

	const handleSaveEdit = useCallback(async (): Promise<void> => {
		if (editingId === null || !validateForm()) return;

		const payload: UpdateCriterionPayload = {
			id: editingId,
			name: formValues.name.trim(),
			description: formValues.description.trim() || undefined,
			rfx_evaluation_type_id: formValues.rfx_evaluation_type_id,
			weight: Number(formValues.weight),
		};
		const result = await editCriterion(editingId, payload);
		if (result) {
			setEditingId(null);
			setFormValues(EMPTY_FORM);
		}
	}, [editingId, validateForm, formValues, editCriterion]);

	return {
		isAdding,
		editingId,
		formValues,
		formError,
		setFormValues,
		updateField,
		handleStartAdd,
		handleStartEdit,
		handleCancel,
		handleSaveNew,
		handleSaveEdit,
	};
};
